import secureApi from '../../utils/secureApi';

export interface KnockoutMatch {
  _id?: string;
  round: string; // e.g., "1/16 Finals", "1/8 Finals", "Quarter Finals", "Semi Finals", "Finals"
  player1?: {
    _id: string;
    name: string;
    shortName?: string;
  };
  player2?: {
    _id: string;
    name: string;
    shortName?: string;
  };
  team1?: {
    _id: string;
    name: string;
    shortName?: string;
    logo?: string;
  };
  team2?: {
    _id: string;
    name: string;
    shortName?: string;
    logo?: string;
  };
  scores?: {
    player1?: number[];
    player2?: number[];
    team1?: number[];
    team2?: number[];
  };
  winner?: {
    _id: string;
    name: string;
    type: 'player' | 'team';
  };
  status?: string; // e.g., "completed", "scheduled", "TBC"
  date?: string;
  time?: string;
  isQualified?: boolean; // For qualification status
}

export interface KnockoutRound {
  roundName: string;
  matches: KnockoutMatch[];
}

export interface KnockoutsResponse {
  statusCode: number;
  status: string;
  message: string;
  data: KnockoutRound[] | {
    rounds: KnockoutRound[];
    ageCategory?: {
      _id: string;
      ageCategory: string;
      format?: string; // 'singles' or 'doubles'
    };
    tournament?: {
      _id: string;
      name: string;
    };
    tournamentSeason?: {
      _id: string;
      name: string;
    };
  };
}

/**
 * Normalize sport name for API URL path
 * Converts "Table Tennis" to "TableTennis", handles casing, etc.
 */
const normalizeSportForUrl = (sport: string): string => {
  // Remove spaces and capitalize first letter of each word
  // "Table Tennis" -> "TableTennis"
  // "Badminton" -> "Badminton"
  // "TT" -> "TT"
  const normalized = sport
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
  
  return normalized;
};

/**
 * Get knockouts data for a tournament age category
 * @param sport - Sport name (e.g., "Badminton", "Table Tennis", "TT")
 * @param tournamentId - Tournament ID
 * @param ageCategoryId - Age Category ID
 * @param tournamentSeasonId - Tournament Season ID (optional)
 * @returns Promise<KnockoutsResponse>
 */
export const getKnockouts = async (
  sport: string,
  tournamentId: string,
  ageCategoryId: string,
  tournamentSeasonId?: string
): Promise<KnockoutsResponse> => {
  try {
    // Normalize sport for URL path (remove spaces, proper casing)
    const normalizedSport = normalizeSportForUrl(sport);
    
    const params: any = {
      tournament: tournamentId,
      ageCategory: ageCategoryId,
      sport: sport, // Keep original sport in query params
    };

    if (tournamentSeasonId) {
      params.tournamentSeason = tournamentSeasonId;
    }

    const apiUrl = `/api/v2/tournaments/website/${normalizedSport}/knockouts`;
    const fullUrl = `${process.env.REACT_APP_API_BASE_URL || ''}${apiUrl}?${new URLSearchParams(params as any).toString()}`;
    
    console.log('Calling knockouts API:', {
      originalSport: sport,
      normalizedSport: normalizedSport,
      apiUrl: apiUrl,
      fullUrl: fullUrl,
      params: params
    });

    const response = await secureApi.get<any>(
      apiUrl,
      { params }
    );

    console.log('Knockouts API raw response:', response);

    // Handle different response structures
    // API returns: { statusCode, status, message, data: [...] } where data is array of rounds
    if (response && response.data) {
      // If data is an array (direct rounds), wrap it in expected structure
      if (Array.isArray(response.data)) {
        return {
          statusCode: response.statusCode || 200,
          status: response.status || 'success',
          message: response.message || 'Knockouts data retrieved successfully',
          data: response.data, // Keep as array for transformation in component
        };
      } else if (response.data.rounds) {
        // If data has rounds property, return as is
        return response as KnockoutsResponse;
      } else {
        // Fallback: wrap response
        return {
          statusCode: response.statusCode || 200,
          status: response.status || 'success',
          message: response.message || 'Knockouts data retrieved successfully',
          data: response.data,
        };
      }
    } else if (response && (response as any).statusCode) {
      return response as KnockoutsResponse;
    } else {
      // If response structure is different, wrap it
      return {
        statusCode: 200,
        status: 'success',
        message: 'Knockouts data retrieved successfully',
        data: response as any,
      };
    }
  } catch (error) {
    console.error('Error fetching knockouts:', error);
    throw error;
  }
};
